/**
 * ReceivePOModal — Flow B receipt entry (Issue #130 / #106)
 *
 * Lets an admin record the actual quantity received per line of a sent PO.
 * Calls the `receive_purchase` MCP tool which routes through the existing
 * receive_purchase_item RPC (creates inventory_batches, advances status).
 *
 * Allows under-shipment: a line can be received with quantity < ordered.
 * The backend leaves the line as 'partial' and the PO stays 'partial'
 * until completed in a follow-up call.
 */

import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Table, Alert, Spinner } from 'react-bootstrap';
import { receivePurchase } from '../../services/purchaseOrderService';
import { toast } from '../../utils/toast';

const today = () => new Date().toISOString().slice(0, 10);

const ReceivePOModal = ({ show, onHide, purchase, onReceived }) => {
  const [rows, setRows] = useState([]);
  const [receivedDate, setReceivedDate] = useState(today());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !purchase) return;
    const items = purchase.items || [];
    setRows(
      items
        .filter((it) => it.status !== 'cancelled')
        .map((it) => {
          const ordered = Number(it.quantity_ordered || 0);
          const already = Number(it.quantity_received || 0);
          const remaining = Math.max(0, ordered - already);
          return {
            item_id: it.id,
            label: it.inventory?.name || it.description || '(sin nombre)',
            sku: it.inventory?.sku || null,
            ordered,
            already,
            remaining,
            quantity: remaining > 0 ? String(remaining) : '0',
            lot_number: it.lot_number || '',
            expiration_date: it.expiration_date || '',
            notes: ''
          };
        })
    );
    setReceivedDate(today());
    setError(null);
  }, [show, purchase]);

  const setRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const handleSubmit = async () => {
    setError(null);
    const received_items = rows
      .filter((r) => Number(r.quantity) > 0)
      .map((r) => ({
        item_id: r.item_id,
        quantity: Number(r.quantity),
        lot_number: r.lot_number || undefined,
        expiration_date: r.expiration_date || undefined,
        notes: r.notes || undefined
      }));

    if (received_items.length === 0) {
      setError('Ingresa al menos una cantidad mayor a 0.');
      return;
    }
    for (const r of rows) {
      const qty = Number(r.quantity || 0);
      if (qty > r.remaining) {
        setError(`No puedes recibir más de lo pendiente para "${r.label}" (máx. ${r.remaining}).`);
        return;
      }
    }

    setSubmitting(true);
    const { error: err, data } = await receivePurchase({
      purchase_id: purchase.id,
      received_items,
      received_date: receivedDate || undefined
    });
    setSubmitting(false);

    if (err) {
      setError(err.message || 'Error al recibir');
      toast.error('Error al recibir: ' + (err.message || ''));
      return;
    }
    toast.success('Mercadería recibida');
    if (onReceived) onReceived(data);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-box-arrow-in-down text-success me-2"></i>
          Recibir mercadería
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3" style={{ maxWidth: 220 }}>
          <Form.Label className="small text-muted mb-1">Fecha de recepción</Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={receivedDate}
            onChange={(e) => setReceivedDate(e.target.value)}
          />
        </Form.Group>

        {rows.length === 0 ? (
          <Alert variant="info">No hay líneas pendientes de recibir.</Alert>
        ) : (
          <div className="table-responsive">
            <Table size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Producto</th>
                  <th className="text-end">Pendiente</th>
                  <th style={{ width: 110 }}>Recibir</th>
                  <th>Lote</th>
                  <th>Vence</th>
                  <th>Notas</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.item_id}>
                    <td>
                      <div className="fw-semibold">{r.sku ? `${r.sku} - ` : ''}{r.label}</div>
                      <small className="text-muted">Ordenadas: {r.ordered} · Ya recibidas: {r.already}</small>
                    </td>
                    <td className="text-end">{r.remaining}</td>
                    <td>
                      <Form.Control
                        type="number"
                        min="0"
                        max={r.remaining}
                        step="1"
                        size="sm"
                        value={r.quantity}
                        onChange={(e) => setRow(idx, { quantity: e.target.value })}
                        disabled={r.remaining === 0}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={r.lot_number}
                        onChange={(e) => setRow(idx, { lot_number: e.target.value })}
                        placeholder="Opcional"
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={r.expiration_date}
                        onChange={(e) => setRow(idx, { expiration_date: e.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        value={r.notes}
                        onChange={(e) => setRow(idx, { notes: e.target.value })}
                        placeholder="Opcional"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="mt-3 mb-0">
            <i className="bi bi-exclamation-triangle me-2"></i>{error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancelar
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={submitting || rows.length === 0}>
          {submitting ? (
            <><Spinner size="sm" className="me-2" />Recibiendo...</>
          ) : (
            <><i className="bi bi-check-circle me-2"></i>Confirmar recepción</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReceivePOModal;
